import React, { useEffect, forwardRef, useState } from "react";
import Editor, { composeDecorators } from 'draft-js-plugins-editor';
import MultiDecorator from "draft-js-plugins-editor/lib/Editor/MultiDecorator";
import { EditorState, convertToRaw, convertFromRaw, CompositeDecorator } from 'draft-js';

import { VideoRender, VideoButton } from './components/video';
import { ImageRender } from './components/image';

import createToolbarPlugin from 'draft-js-static-toolbar-plugin';
import createImagePlugin from 'draft-js-image-plugin';
import createResizeablePlugin from '@draft-js-plugins/resizeable';
import createFocusPlugin from '@draft-js-plugins/focus';
import createAlignmentPlugin from '@draft-js-plugins/alignment';
import createVideoPlugin from '@draft-js-plugins/video';

import createEmbedVideoPlugin from './EditorPlugin/video-embed';
import createPasteImagePlugin from './EditorPlugin/paste-image';

import {
    HeadlineOneButton,
    HeadlineTwoButton,
    BoldButton,
    ItalicButton,
    UnderlineButton,
    OrderedListButton,
    UnorderedListButton
} from 'draft-js-buttons';

import editorStyle from './style/editor.module.css';
import buttonStyles from './style/button.module.css';
import toolbarStyles from './style/toolbar.module.css';
import alignmentStyle from './style/alignment.module.css';

import createLinkDetectionPlugin from './EditorPlugin/paste-link';
import createLinkPlugin from '@draft-js-plugins/anchor';
import createInlineToolbarPlugin from '@draft-js-plugins/inline-toolbar';
import "@draft-js-plugins/anchor/lib/plugin.css";
import "@draft-js-plugins/inline-toolbar/lib/plugin.css";


const toolbarPlugin = createToolbarPlugin({
    theme: { buttonStyles, toolbarStyles }
});
const focusPlugin = createFocusPlugin();
const resizeablePlugin = createResizeablePlugin();
const alignmentPlugin = createAlignmentPlugin({
    theme: {
        alignmentToolStyles: alignmentStyle,
        buttonStyles
    },
});
const { AlignmentTool } = alignmentPlugin;

const decorator = composeDecorators(
    focusPlugin.decorator
);

const imagePlugin = createImagePlugin({
    decorator,
    imageComponent: ImageRender
});
const videoPlugin = createVideoPlugin({
    videoComponent: VideoRender
});
const embedVideoPlugin = createEmbedVideoPlugin();
const pasteImagePlugin = createPasteImagePlugin();
const linkDetectionPlugin = createLinkDetectionPlugin({
    target: '_blank',
});
const linkPlugin = createLinkPlugin();
const inlineToolbarPlugin = createInlineToolbarPlugin();
const { InlineToolbar } = inlineToolbarPlugin

export const getPlugins = (enableLink = false) => [
    resizeablePlugin,
    alignmentPlugin,
    focusPlugin,
    toolbarPlugin,
    imagePlugin,
    videoPlugin,
    embedVideoPlugin,
    pasteImagePlugin,
    ...(enableLink ? [linkDetectionPlugin, linkPlugin, inlineToolbarPlugin] : []),
];

const getPluginDecoratorArray = (plugins) => {
    return plugins.reduce((decorators, plugin) => decorators.concat(plugin.decorators || []), []);
}

export const getPluginDecorators = (enableLink = false) => new MultiDecorator([new CompositeDecorator(getPluginDecoratorArray(getPlugins(enableLink))),]);

const Separator = () => (<div style={{ background: '#ffffff4d', marginLeft: 6, width: 1, height: 18 }} />)

const myBlockStyleFn = (contentBlock) => {
    const type = contentBlock.getType();
    if (type === 'header-one') {
        return editorStyle.title;
    }
    if (type === 'header-two') {
        return editorStyle.subtitle;
    }
}
const { Toolbar } = toolbarPlugin;

const SlideEditor = forwardRef(
    ({
        name,
        defaultValue,
        width,
        showModalSection = 'Announcements',
        metricVideo = [],
        update = false,
        setValue,
        register,
        enableLink = false
    }, ref) => {


        //let content = defaultValue ?  EditorState.createWithContent(convertFromRaw(JSON.parse(defaultValue)))  :  EditorState.createWithContent(convertFromRaw(initialState));
        // let content = defaultValue ?  EditorState.createWithContent(convertFromRaw(JSON.parse(defaultValue)))  :  EditorState.createEmpty();
        const [editorState, setEditorState] = useState(EditorState.createEmpty());
        const [isEditorReady, setIsEditorReady] = useState(false);

        const editor = React.useRef(null);

        /* register field once */
        useEffect(() => {
            register(name);
        }, [register, name]);

        useEffect(() => {

            if (!isEditorReady) return;

            const frameId = requestAnimationFrame(() => {
                editor.current?.focus();
            });

            return () => cancelAnimationFrame(frameId);
        }, [isEditorReady]);

        useEffect(() => {
            if (defaultValue) {
                let newDefaultValue = JSON.parse(defaultValue);
                let entityMap = newDefaultValue?.entityMap;
                if (entityMap) {
                    for (let key in entityMap) {
                        if (entityMap[key].data.type === "metricVideo") {
                            let videoObj = metricVideo.find((video) => video.metricKey === entityMap[key].data.src);
                            if (videoObj !== undefined) {
                                let vimeoUrl = videoObj?.videoUrl ? videoObj?.videoUrl : '';
                                newDefaultValue.entityMap[key].data.src = vimeoUrl;
                            }
                        }
                    }
                }
                const contentState = EditorState.createWithContent(convertFromRaw(newDefaultValue), getPluginDecorators(enableLink))
                setEditorState(contentState)
                setValue(name, JSON.stringify(newDefaultValue));
            }
            setIsEditorReady(true);
        }, [defaultValue, metricVideo, name, update])

        useEffect(() => {
            const value = JSON.stringify(convertToRaw(editorState.getCurrentContent()));
            setValue(name, value);
        }, [editorState, name])

        return (
            <>
                <div style={{ width: width ? width : "auto", position: "relative" }} className={editorStyle.editor}>
                    <Editor
                        ref={el => editor.current = el}
                        editorState={editorState}
                        onChange={setEditorState}
                        onBlur={() => {
                            const value = JSON.stringify(convertToRaw(editorState.getCurrentContent()));
                            setValue(name, value);
                        }}
                        plugins={getPlugins(enableLink)}
                        blockStyleFn={myBlockStyleFn}
                    />
                    {enableLink &&
                        <InlineToolbar>
                            {(externalProps) => (
                                <>
                                    <BoldButton {...externalProps} />
                                    <ItalicButton {...externalProps} />
                                    <UnderlineButton {...externalProps} />
                                    <linkPlugin.LinkButton {...externalProps} />
                                </>
                            )}
                        </InlineToolbar>
                    }
                    <AlignmentTool />
                    <Toolbar>
                        {(props) => {
                            return (
                                <div>
                                    <HeadlineOneButton {...props} />
                                    <HeadlineTwoButton {...props} />
                                    <Separator />
                                    <BoldButton {...props} />
                                    <ItalicButton {...props} />
                                    <UnderlineButton {...props} />
                                    <Separator />
                                    <OrderedListButton {...props} />
                                    <UnorderedListButton {...props} />
                                    <Separator />
                                    <VideoButton
                                        {...props}
                                        modifier={videoPlugin.addVideo}
                                        showModalSection={showModalSection}
                                        metricVideo={metricVideo}
                                    />
                                </div>
                            );
                        }}
                    </Toolbar>
                </div>
            </>
        );
    }
);


export { SlideEditor }